<template>
  <div id="integrationsSlack" :class="{ 'panel-mode': panelMode }">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading"></i>
      <span class="ml-8">{{ $t('integrations.slack.loadingConfig') }}</span>
    </div>

    <!-- Not Connected State -->
    <div v-else-if="!isConnected" class="not-connected-state">
      <div class="slack-hero">
        <div class="slack-logo-large">
          <svg viewBox="0 0 124 124" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M26.4 78.9c0 7.2-5.8 13-13 13s-13-5.8-13-13 5.8-13 13-13h13v13zm6.5 0c0-7.2 5.8-13 13-13s13 5.8 13 13v32.5c0 7.2-5.8 13-13 13s-13-5.8-13-13V78.9z" fill="#E01E5A"/>
            <path d="M45.9 26.4c-7.2 0-13-5.8-13-13s5.8-13 13-13 13 5.8 13 13v13h-13zm0 6.5c7.2 0 13 5.8 13 13s-5.8 13-13 13H13.4c-7.2 0-13-5.8-13-13s5.8-13 13-13h32.5z" fill="#36C5F0"/>
            <path d="M98.1 45.9c0-7.2 5.8-13 13-13s13 5.8 13 13-5.8 13-13 13h-13v-13zm-6.5 0c0 7.2-5.8 13-13 13s-13-5.8-13-13V13.4c0-7.2 5.8-13 13-13s13 5.8 13 13v32.5z" fill="#2EB67D"/>
            <path d="M78.6 98.1c7.2 0 13 5.8 13 13s-5.8 13-13 13-13-5.8-13-13v-13h13zm0-6.5c-7.2 0-13-5.8-13-13s5.8-13 13-13h32.5c7.2 0 13 5.8 13 13s-5.8 13-13 13H78.6z" fill="#ECB22E"/>
          </svg>
        </div>
        <h2 class="hero-title">{{ $t('integrations.slack.connectTitle') }}</h2>
        <p class="hero-description">
          {{ $t('integrations.slack.connectDescription') }}
        </p>
      </div>

      <div class="features-preview">
        <div class="feature-item">
          <span class="feature-icon">🌟</span>
          <div class="feature-text">
            <strong>{{ $t('integrations.slack.feature.recognitionTitle') }}</strong>
            <span>{{ $t('integrations.slack.feature.recognitionDesc') }}</span>
          </div>
        </div>
        <div class="feature-item">
          <span class="feature-icon">📊</span>
          <div class="feature-text">
            <strong>{{ $t('integrations.slack.feature.reportsTitle') }}</strong>
            <span>{{ $t('integrations.slack.feature.reportsDesc') }}</span>
          </div>
        </div>
        <div class="feature-item">
          <span class="feature-icon">💭</span>
          <div class="feature-text">
            <strong>{{ $t('integrations.slack.feature.nudgesTitle') }}</strong>
            <span>{{ $t('integrations.slack.feature.nudgesDesc') }}</span>
          </div>
        </div>
        <div class="feature-item">
          <span class="feature-icon">🎉</span>
          <div class="feature-text">
            <strong>{{ $t('integrations.slack.feature.reactionTitle') }}</strong>
            <span>{{ $t('integrations.slack.feature.reactionDesc') }}</span>
          </div>
        </div>
      </div>

      <div class="connect-action">
        <el-button
          type="primary"
          class="btn-connect"
          @click="connectSlack"
          :loading="connecting"
        >
          <i v-if="!connecting" class="el-icon-link"></i>
          <span>{{ connecting ? $t('integrations.slack.connecting') : $t('integrations.slack.connectButton') }}</span>
        </el-button>
        <p class="connect-note">
          {{ $t('integrations.slack.redirectNote') }}
        </p>
      </div>

      <!-- Coming Soon Notice (shown when API not ready) -->
      <div v-if="showComingSoon" class="coming-soon-notice">
        <i class="el-icon-info-circle"></i>
        <span>{{ $t('integrations.slack.comingSoon') }}</span>
      </div>
    </div>

    <!-- Connected State -->
    <div v-else class="connected-state">
      <!-- Connection Status -->
      <div class="connection-status">
        <div class="status-header">
          <div class="status-icon connected">
            <i class="el-icon-circle-check"></i>
          </div>
          <div class="status-info">
            <h3>{{ $t('integrations.slack.connectedTo') }} {{ workspaceName }}</h3>
            <p v-if="configuration && configuration.installedAt">
              {{ $t('integrations.slack.connectedOn') }} {{ formatDate(configuration.installedAt) }}
              <span v-if="configuration.installedBy"> {{ $t('integrations.slack.byUser') }} {{ configuration.installedBy }}</span>
            </p>
          </div>
        </div>
        <div class="status-actions">
          <el-button size="small" @click="handleTestConnection" :loading="testingConnection">
            <i v-if="!testingConnection" class="el-icon-refresh"></i>
            {{ $t('integrations.slack.test') }}
          </el-button>
          <el-button size="small" type="danger" plain @click="confirmDisconnect">
            {{ $t('integrations.slack.disconnect') }}
          </el-button>
        </div>
      </div>

      <!-- Feature Toggles -->
      <div class="config-section">
        <h4 class="section-title">{{ $t('integrations.slack.featuresSection') }}</h4>

        <div class="toggle-row">
          <div class="toggle-info">
            <span class="toggle-icon">🌟</span>
            <div>
              <span class="toggle-label">{{ $t('integrations.slack.feature.recognitionTitle') }}</span>
              <span class="toggle-desc">{{ $t('integrations.slack.toggle.recognitionDesc') }}</span>
            </div>
          </div>
          <el-switch
            v-model="localConfig.features.recognitionBroadcast"
            active-color="#6C30CC"
          />
        </div>

        <div class="toggle-row">
          <div class="toggle-info">
            <span class="toggle-icon">📊</span>
            <div>
              <span class="toggle-label">{{ $t('integrations.slack.toggle.dailyReportTitle') }}</span>
              <span class="toggle-desc">{{ $t('integrations.slack.toggle.dailyReportDesc') }}</span>
            </div>
          </div>
          <el-switch
            v-model="localConfig.features.reportSnippets.daily"
            active-color="#6C30CC"
          />
        </div>

        <div class="toggle-row">
          <div class="toggle-info">
            <span class="toggle-icon">📈</span>
            <div>
              <span class="toggle-label">{{ $t('integrations.slack.toggle.weeklyReportTitle') }}</span>
              <span class="toggle-desc">{{ $t('integrations.slack.toggle.weeklyReportDesc') }}</span>
            </div>
          </div>
          <el-switch
            v-model="localConfig.features.reportSnippets.weekly"
            active-color="#6C30CC"
          />
        </div>

        <div class="toggle-row">
          <div class="toggle-info">
            <span class="toggle-icon">💭</span>
            <div>
              <span class="toggle-label">{{ $t('integrations.slack.feature.nudgesTitle') }}</span>
              <span class="toggle-desc">{{ $t('integrations.slack.toggle.nudgesDesc') }}</span>
            </div>
          </div>
          <el-switch
            v-model="localConfig.features.nudges.enabled"
            active-color="#6C30CC"
          />
        </div>

        <div class="toggle-row">
          <div class="toggle-info">
            <span class="toggle-icon">🎉</span>
            <div>
              <span class="toggle-label">{{ $t('integrations.slack.feature.reactionTitle') }}</span>
              <span class="toggle-desc">{{ $t('integrations.slack.toggle.reactionDesc') }}</span>
            </div>
          </div>
          <el-switch
            v-model="localConfig.features.reactionSync"
            active-color="#6C30CC"
          />
        </div>
      </div>

      <!-- Channel Settings (when recognition broadcasting is enabled) -->
      <div v-if="localConfig.features.recognitionBroadcast" class="config-section">
        <h4 class="section-title">{{ $t('integrations.slack.channelSettings') }}</h4>

        <div class="setting-row">
          <span class="setting-label">{{ $t('integrations.slack.recognitionChannel') }}</span>
          <el-select
            v-model="localConfig.channels.recognition.channelId"
            :placeholder="$t('integrations.slack.selectChannel')"
            size="small"
            class="channel-select"
            @change="updateRecognitionChannel"
          >
            <el-option
              v-for="channel in availableChannels"
              :key="channel.id"
              :label="'#' + channel.name"
              :value="channel.id"
            />
          </el-select>
        </div>

        <div class="checkbox-row">
          <el-checkbox v-model="localConfig.channels.teamChannels">
            {{ $t('integrations.slack.teamChannels') }}
          </el-checkbox>
        </div>

        <div class="checkbox-row">
          <el-checkbox v-model="localConfig.channels.dmRecipient">
            {{ $t('integrations.slack.dmRecipients') }}
          </el-checkbox>
        </div>
      </div>

      <!-- Nudge Settings (when nudges enabled) -->
      <div v-if="localConfig.features.nudges.enabled" class="config-section">
        <h4 class="section-title">{{ $t('integrations.slack.nudgeSettings') }}</h4>

        <div class="setting-row">
          <span class="setting-label">{{ $t('integrations.slack.triggerAfter') }}</span>
          <div class="inline-setting">
            <el-input-number
              v-model="localConfig.features.nudges.triggerDays"
              :min="1"
              :max="7"
              size="small"
              controls-position="right"
            />
            <span class="setting-suffix">{{ $t('integrations.slack.daysWithoutCheckin') }}</span>
          </div>
        </div>

        <div class="setting-row">
          <span class="setting-label">{{ $t('integrations.slack.maxPerWeek') }}</span>
          <div class="inline-setting">
            <el-input-number
              v-model="localConfig.features.nudges.maxPerWeek"
              :min="1"
              :max="5"
              size="small"
              controls-position="right"
            />
            <span class="setting-suffix">{{ $t('integrations.slack.nudgesPerPerson') }}</span>
          </div>
        </div>
      </div>

      <!-- Save Button -->
      <div class="save-section">
        <el-button
          type="primary"
          class="btn-save"
          @click="saveChanges"
          :loading="saving"
          :disabled="!hasChanges"
        >
          {{ saving ? $t('integrations.slack.saving') : $t('integrations.Save_Changes') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'SlackIntegration',

  props: {
    panelMode: {
      type: Boolean,
      default: false
    }
  },

  data: function() {
    return {
      connecting: false,
      testingConnection: false,
      saving: false,
      showComingSoon: false,
      localConfig: {
        features: {
          recognitionBroadcast: true,
          reportSnippets: { daily: true, weekly: true },
          nudges: { enabled: true, triggerDays: 3, maxPerWeek: 2 },
          reactionSync: true
        },
        channels: {
          recognition: { channelId: '', channelName: '', enabled: true },
          teamChannels: false,
          dmRecipient: false
        }
      },
      originalConfig: null
    }
  },

  computed: {
    ...mapGetters('slack', [
      'isConnected',
      'workspaceName',
      'configuration',
      'loading',
      'error',
      'availableChannels'
    ]),

    hasChanges: function() {
      if (!this.originalConfig) return false
      return JSON.stringify(this.localConfig) !== JSON.stringify(this.originalConfig)
    }
  },

  created: function() {
    this.init()
  },

  methods: {
    ...mapActions('slack', [
      'fetchConfiguration',
      'initiateOAuth',
      'disconnect',
      'fetchChannels',
      'updateConfiguration'
    ]),

    init: function() {
      var self = this
      this.fetchConfiguration()
        .then(function() {
          if (self.isConnected) {
            self.syncLocalConfig()
            self.fetchChannels()
          }
        })
        .catch(function(error) {
          console.error('Failed to initialize Slack integration:', error)
        })
    },

    syncLocalConfig: function() {
      if (this.configuration) {
        this.localConfig = JSON.parse(JSON.stringify({
          features: this.configuration.features || this.localConfig.features,
          channels: this.configuration.channels || this.localConfig.channels
        }))
        this.originalConfig = JSON.parse(JSON.stringify(this.localConfig))
      }
    },

    connectSlack: function() {
      var self = this
      this.connecting = true
      this.showComingSoon = false

      this.initiateOAuth()
        .then(function(oauthUrl) {
          if (oauthUrl) {
            window.location.href = oauthUrl
          }
        })
        .catch(function(error) {
          console.error('OAuth error:', error)
          // Check if it's a 404 (backend not ready)
          if (error && error.response && error.response.status === 404) {
            self.showComingSoon = true
          } else if (error && error.message && error.message.indexOf('404') !== -1) {
            self.showComingSoon = true
          } else {
            self.$message.error('Failed to connect to Slack. Please try again.')
          }
        })
        .finally(function() {
          self.connecting = false
        })
    },

    confirmDisconnect: function() {
      var self = this
      this.$confirm(
        'This will disconnect Slack from Happily. Recognition broadcasting, report snippets, and nudges will stop working.',
        'Disconnect Slack?',
        {
          confirmButtonText: 'Disconnect',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(function() {
        return self.disconnect()
      }).then(function() {
        self.$message.success('Slack disconnected')
        self.$emit('close')
      }).catch(function() {
        // Cancelled or error
      })
    },

    handleTestConnection: function() {
      var self = this
      this.testingConnection = true

      this.$store.dispatch('slack/testConnection')
        .then(function(result) {
          if (result && result.success) {
            self.$message.success('Connection is working!')
          } else {
            self.$message.warning('Connection test failed')
          }
        })
        .catch(function() {
          self.$message.error('Could not test connection')
        })
        .finally(function() {
          self.testingConnection = false
        })
    },

    updateRecognitionChannel: function(channelId) {
      var channel = this.availableChannels.find(function(c) { return c.id === channelId })
      if (channel) {
        this.localConfig.channels.recognition = {
          channelId: channel.id,
          channelName: channel.name,
          enabled: true
        }
      }
    },

    saveChanges: function() {
      var self = this
      this.saving = true

      this.updateConfiguration({
        features: this.localConfig.features,
        channels: this.localConfig.channels
      })
        .then(function() {
          self.$message.success('Settings saved')
          self.originalConfig = JSON.parse(JSON.stringify(self.localConfig))
        })
        .catch(function() {
          self.$message.error('Failed to save settings')
        })
        .finally(function() {
          self.saving = false
        })
    },

    formatDate: function(dateString) {
      if (!dateString) return ''
      try {
        var parts = dateString.split(' ')[0].split('-')
        var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        return months[parseInt(parts[1], 10) - 1] + ' ' + parseInt(parts[2], 10) + ', ' + parts[0]
      } catch (e) {
        return dateString
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#integrationsSlack {
  font-family: 'Lato', 'Kanit', Arial, sans-serif;
  &.panel-mode {
    // Styles specific to dialog/panel mode
  }
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6b6b6b;
  font-size: 15px;
}

/* Not Connected State */
.not-connected-state {
  text-align: center;
}

.slack-hero {
  padding: 20px 0 24px;
}

.slack-logo-large {
  width: 72px;
  height: 72px;
  margin: 0 auto 16px;

  svg {
    width: 100%;
    height: 100%;
  }
}

.hero-title {
  font-size: 22px;
  font-weight: 700;
  color: #3b3b3b;
  margin: 0 0 8px;
}

.hero-description {
  font-size: 15px;
  color: #6b6b6b;
  margin: 0;
  max-width: 360px;
  margin: 0 auto;
  line-height: 1.5;
}

.features-preview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 24px 0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  margin: 0 0 24px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  text-align: left;
  padding: 12px;
  border-radius: 8px;
  background: #fafafa;
}

.feature-icon {
  font-size: 20px;
  margin-right: 10px;
  flex-shrink: 0;
}

.feature-text {
  display: flex;
  flex-direction: column;

  strong {
    font-size: 13px;
    font-weight: 600;
    color: #3b3b3b;
    margin-bottom: 2px;
  }

  span {
    font-size: 12px;
    color: #909399;
    line-height: 1.4;
  }
}

.connect-action {
  padding-bottom: 8px;
}

.btn-connect {
  height: 48px;
  padding: 0 32px;
  font-size: 16px;
  font-weight: 600;
  background-color: #6C30CC;
  border-color: #6C30CC;

  i {
    margin-right: 8px;
  }

  &:hover {
    background-color: #5a28a8;
    border-color: #5a28a8;
  }
}

.connect-note {
  font-size: 13px;
  color: #909399;
  margin: 12px 0 0;
}

.coming-soon-notice {
  margin-top: 16px;
  padding: 12px 16px;
  background: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 6px;
  color: #e6a23c;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* Connected State */
.connected-state {
  // Full config UI when connected
}

.connection-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  margin-bottom: 24px;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;

  &.connected {
    background: #dcfce7;
    color: #16a34a;
  }
}

.status-info {
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #166534;
    margin: 0 0 2px;
  }

  p {
    font-size: 13px;
    color: #4ade80;
    margin: 0;
  }
}

.status-actions {
  display: flex;
  gap: 8px;
}

.config-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 12px;
  font-weight: 700;
  color: #9d9d9d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
}

.toggle-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-icon {
  font-size: 20px;
  width: 32px;
  text-align: center;
}

.toggle-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #3b3b3b;
}

.toggle-desc {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
}

.setting-label {
  font-size: 14px;
  color: #4a4a4a;
}

.inline-setting {
  display: flex;
  align-items: center;
  gap: 8px;
}

.setting-suffix {
  font-size: 13px;
  color: #909399;
}

.channel-select {
  width: 200px;
}

.checkbox-row {
  padding: 8px 0;

  .el-checkbox {
    font-size: 14px;
  }
}

.save-section {
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.btn-save {
  width: 100%;
  height: 44px;
  font-size: 15px;
  font-weight: 600;
  background-color: #6C30CC;
  border-color: #6C30CC;

  &:hover:not(:disabled) {
    background-color: #5a28a8;
    border-color: #5a28a8;
  }

  &:disabled {
    background-color: #c9c9c9;
    border-color: #c9c9c9;
  }
}
</style>

<style lang="scss">
#integrationsSlack {
  .el-switch.is-checked .el-switch__core {
    background-color: #6C30CC;
    border-color: #6C30CC;
  }

  .el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: #6C30CC;
    border-color: #6C30CC;
  }

  .el-checkbox__input.is-checked + .el-checkbox__label {
    color: #3b3b3b;
  }

  .el-input-number--small {
    width: 90px;
  }

  .el-button--danger.is-plain {
    color: #f56c6c;
    border-color: #fbc4c4;

    &:hover {
      background: #f56c6c;
      border-color: #f56c6c;
      color: #fff;
    }
  }
}
</style>
